import React, { useState } from "react";
import { toast } from "react-toastify";

import {
  ADJUSTMENT_REASON_LABELS,
  AdjustmentReason,
  BulkPlan,
  createBulkAdjustmentApi,
  DP_ADJUSTMENT_REASONS,
  hhmm,
} from "../api/timekeepingApi";
import { formatDateOnly } from "../utils/format";

type Props = {
  token: string;
  contractId: string;
  employeeName: string;
  /** Sugestões do intervalo (YYYY-MM-DD). */
  defaultFrom: string;
  defaultTo: string;
  /** Acesso ao app criado depois da admissão: sugere o motivo "Sem acesso ao app". */
  suggestNoCredential?: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type Row = { day: string; times: string[]; skipped: string | null };

const LABELS = ["Entrada", "Saída intervalo", "Volta intervalo", "Saída"];
const toMinute = (value: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Lote do DP: marcações de vários dias de uma vez (colaborador sem acesso ao
 * app, celular quebrado por dias, sistema fora do ar). O intervalo é
 * pré-preenchido pela escala; o DP revisa a grade antes de lançar. Tudo entra
 * aprovado, num lote só — que pode ser anulado inteiro depois.
 */
export default function BulkTimeAdjustmentModal({ token, contractId, employeeName, defaultFrom, defaultTo, suggestNoCredential, onClose, onSaved }: Props) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [reasonCode, setReasonCode] = useState<AdjustmentReason>(suggestNoCredential ? "SEM_CREDENCIAL" : "DISPOSITIVO_INDISPONIVEL");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);

  const prefill = async () => {
    if (!from || !to) return toast.error("Informe o período.");
    setBusy(true);
    try {
      const plan = await createBulkAdjustmentApi<BulkPlan>(token, contractId, { reasonCode, from, to, dryRun: true });
      setRows(plan.days.map((d) => ({ day: d.day, skipped: d.skipped, times: [0, 1, 2, 3].map((i) => (d.marks[i] !== undefined ? hhmm(d.marks[i]) : "")) })));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao montar a grade.");
    } finally {
      setBusy(false);
    }
  };

  const setTime = (day: string, index: number, value: string) =>
    setRows((list) => (list ?? []).map((r) => (r.day === day ? { ...r, times: r.times.map((t, i) => (i === index ? value : t)) } : r)));

  const save = async () => {
    if (!rows) return;
    if (reasonCode === "OUTRO" && reason.trim().length < 10) return toast.error("Com o motivo \"Outro\", descreva o que aconteceu (mínimo de 10 caracteres).");
    const days = rows
      .filter((r) => !r.skipped)
      .map((r) => ({ day: r.day, marks: r.times.map(toMinute).filter((m): m is number => m !== null) }))
      .filter((d) => d.marks.length);
    if (!days.length) return toast.error("Nenhum dia com horários.");
    const odd = days.find((d) => d.marks.length % 2 === 1);
    if (odd) return toast.error(`${formatDateOnly(odd.day)}: informe os horários em pares (entrada e saída).`);
    setBusy(true);
    try {
      const res = await createBulkAdjustmentApi(token, contractId, { reasonCode, reason: reason.trim() || undefined, days });
      toast.success(`${res.created} marcação(ões) lançada(s) em ${res.days} dia(s).`);
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao lançar o lote.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Lançar marcações em lote · {employeeName}</h2>
          <button type="button" className="dpModal__close" onClick={onClose} disabled={busy}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="bulkFrom">De *</label>
              <input id="bulkFrom" type="date" className="dpInput" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="dpFormGroup">
              <label htmlFor="bulkTo">Até *</label>
              <input id="bulkTo" type="date" className="dpInput" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="dpFormGroup">
              <label htmlFor="bulkReason">Motivo *</label>
              <select id="bulkReason" className="dpSelect" value={reasonCode} onChange={(e) => setReasonCode(e.target.value as AdjustmentReason)}>
                {DP_ADJUSTMENT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {ADJUSTMENT_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="dpFormGroup">
            <label htmlFor="bulkText">Observação{reasonCode === "OUTRO" ? " *" : ""}</label>
            <textarea id="bulkText" className="dpTextarea" maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {reasonCode === "SEM_CREDENCIAL" ? (
            <p className="dpFormHint">"Sem acesso ao app" só vale para dias anteriores à criação do acesso do colaborador.</p>
          ) : null}

          <button type="button" className="dpBtn dpBtn--secondary" onClick={prefill} disabled={busy}>
            {rows ? "Montar a grade de novo" : "Pré-preencher pela escala"}
          </button>

          {rows ? (
            <div className="dpTkBulk" style={{ marginTop: 12 }}>
              <div className="dpTkBulkRow dpMuted">
                <span>Dia</span>
                {LABELS.map((l) => (
                  <span key={l}>{l}</span>
                ))}
              </div>
              {rows.map((r) => (
                <div key={r.day} className={`dpTkBulkRow ${r.skipped ? "dpTkBulkRow--skip" : ""}`}>
                  <span>
                    {formatDateOnly(r.day)} {new Date(`${r.day}T00:00:00Z`).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short" })}
                  </span>
                  {r.skipped ? (
                    <span style={{ gridColumn: "span 4" }}>{r.skipped} — pulado</span>
                  ) : (
                    r.times.map((t, i) => (
                      <input
                        key={i}
                        type="time"
                        className="dpInput"
                        aria-label={`${LABELS[i]} de ${formatDateOnly(r.day)}`}
                        value={t}
                        onChange={(e) => setTime(r.day, i, e.target.value)}
                      />
                    ))
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={save} disabled={busy || !rows}>
            {busy ? "Aguarde..." : "Lançar o lote"}
          </button>
        </footer>
      </div>
    </div>
  );
}
